import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router';
import axios from 'axios';
import { authOperations } from 'src/store/modules/auth';
import Loading from '../common/Loading';
import BeaconSignInContainer from './BeaconSignIn';
import SignIn from './SignIn';
import DivisionSelector from './DivisionSelector';
import {
  v2IssueToken,
  v2SessionInfo,
  v2OtpSend,
  v2OtpVerify,
  V2LoginResponse,
  resolveDefaultDivision
} from 'src/store/modules/auth/v2Operations';
import { redirectToCognito } from 'src/utils/cognitoAuth';
import { saveDivisions, saveCurrentDivisionId } from 'src/utils/divisionStorage';
import OtpVerify from './OtpVerify';
import NoWorkspace, { isNoWorkspaceError } from './NoWorkspace';
import {
  getAppUrlForReferrer,
  getReferrerInfo,
  getApiHost,
  setAuthFlowValue,
  getAuthFlowValue,
  clearAuthFlowData,
  isSsoHost
} from 'src/utils/ssoReferrer';
import { evaluateV2ForEmail } from 'src/utils/authVersion';
import { initializeAmplify, amplifyFederatedSignIn, safelyFetchAuthSession } from 'src/utils/auth';
import { setCognitoIdToken } from 'src/services/advisorContext/cognitoTokenStore';
import i18n from 'src/i18n_en';
import './SignIn.scss';

const V1_PENDING_AUTH_KEY = 'sl-pending-auth-flow';

type AuthStep =
  | 'email'
  | 'redirecting'
  | 'division_select'
  | 'issuing_token'
  | 'otp_verify'
  | 'v1_redirect'
  // Credential accepted, but the account has no usable workspace. Terminal:
  // with no division the API cannot mint a JWT, so nothing is signed in here.
  | 'no_workspace';

interface SignInV2ContainerProps extends RouteComponentProps {
  app: any;
  auth: any;
  clearErrorMessage: () => void;
  setErrorMessage: (msg: string) => void;
}

/**
 * Append a V1 SSO JWT to a redirect URL as ?jwt_token=…
 *
 * Required for Advisor (zenith-ui) V1 flow: AuthProvider.tsx +
 * simple-auth.handleAuthCallback() reads jwt_token from the URL and
 * stores it in localStorage as nova-jwt-token. Atlas/Beacon V1 ignore
 * this query param and rely on .stackline.com session cookies.
 */
function appendJwtToken(url: string, jwtToken?: string): string {
  if (!jwtToken) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}jwt_token=${encodeURIComponent(jwtToken)}`;
}

/**
 * Append a Cognito idToken to a redirect URL as ?cognito_id_token=…
 *
 * Required for V1 sessions when sign-in happens on enterprise-sso.stackline.com:
 * Atlas/Beacon authenticate to Advisor (zenith-service) with `Authorization:
 * Bearer <cognitoIdToken>`, but neither the Amplify session nor the
 * sl-cognito-id-token sessionStorage entry survives the cross-origin redirect
 * from enterprise-sso → atlas/beacon. Forwarding the token via URL lets the
 * target app's bootstrap (main.tsx) persist it via setCognitoIdToken before
 * any advisor call fires.
 */
function appendCognitoIdToken(url: string, cognitoIdToken?: string): string {
  if (!cognitoIdToken) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}cognito_id_token=${encodeURIComponent(cognitoIdToken)}`;
}

// Cross-app SSO consumers (e.g. GitBook) that need a Stackline-Inc-issued
// SSO JWT in the redirect URL. V2 token issuance does not produce one;
// we fall back to V1 GetConfig which accepts the V2 enterprise auth cookie
// (CognitoJwtHelpers.OnTokenValidated treats V1 SSO and V2 enterprise tokens
// the same at the JWT bearer layer, so V1 GetConfig works for V2 sessions).
//
// TODO(v1-deprecation): Remove this entire bridge once V1 auth is retired.
//   When the V1 SSO and Cognito branches in
//   api/Stackline.Auth/CognitoJwtHelpers.cs OnTokenValidated are removed,
//   /api/user/GetConfig will start returning 401 for V2 sessions and the
//   GitBook flow will break again. Migration steps:
//     1. API: extend AuthV2Controller.IssueTokenAsync to also call
//        SSOTokenService when request.Referrer is an SSO-only target,
//        and add `SsoJwtToken` to Models/Auth/V2/TokenResponse.cs.
//     2. Website: in handleDivisionSelect below, read
//        response.data?.ssoJwtToken from v2IssueToken() instead of
//        calling fetchSsoJwtToken().
//     3. Website: in checkExistingAuth's "already authenticated" branch,
//        call a new V2 endpoint (e.g. POST /api/auth/v2/sso-token) that
//        signs the SSO JWT for the current V2 session.
//     4. Delete SSO_ONLY_REFERRERS, fetchSsoJwtToken, and every
//        `v1-deprecation` TODO in this file.
//   Grep `v1-deprecation` across the website repo for every call site.
const SSO_ONLY_REFERRERS = new Set(['gitbook', 'stackline-support-portal']);

async function fetchSsoJwtToken(referrer: string): Promise<string | undefined> {
  const v1Referrer = referrer.replace(/-(?:localhost|dev|qa|alpha|staging|beta)$/, '');
  if (!SSO_ONLY_REFERRERS.has(v1Referrer)) return undefined;
  try {
    const res = await axios.get(`https://${getApiHost()}/api/user/GetConfig`, {
      params: { ssoReferrer: v1Referrer },
      withCredentials: true
    });
    return res.data?.ssoJwtToken || undefined;
  } catch (err) {
    console.error('[SignIn] Failed to fetch SSO JWT for referrer:', v1Referrer, err);
    return undefined;
  }
}

/**
 * Resolve the effective referrer string (with stage suffix) from URL params / localStorage.
 */
/**
 * The referrer the sign-in actually carried, before any fallback — null when
 * there was none.
 *
 * `resolveReferrer` cannot answer this: it substitutes 'atlas', and that
 * fabricated value used to be written into the OAuth `state` before the
 * redirect to Cognito, so by the time AuthCallback ran it looked like real app
 * context and sent a context-free sign-in to Atlas. Anything deciding WHERE to
 * send the user afterwards must ask this, not resolveReferrer().
 */
function rawReferrer(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('referrer') || getAuthFlowValue('x-sl-sso-signin-referrer') || null;
}

function resolveReferrer(): string {
  const urlParams = new URLSearchParams(window.location.search);
  let referrer = urlParams.get('referrer') || getAuthFlowValue('x-sl-sso-signin-referrer') || 'atlas';

  const { stage } = getReferrerInfo();
  if (stage !== 'prod' && !referrer.includes(`-${stage}`)) {
    referrer = `${referrer}-${stage}`;
  }
  return referrer;
}

// Map referrer app names to their valid hostname patterns
const REFERRER_HOSTNAME_PATTERNS: Record<string, string[]> = {
  advisor: ['advisor'],
  gitbook: ['enterprise-help'],
  'stackline-support-portal': ['enterprise-help']
};

function resolveReturnUrl(referrer?: string): string | undefined {
  const urlParams = new URLSearchParams(window.location.search);
  const urlReturn = urlParams.get('returnUrl');
  const storedReturn = getAuthFlowValue('x-sl-sso-signin-return-url');

  const returnUrl = urlReturn || storedReturn || undefined;

  // Validate that the returnUrl domain matches the referrer to prevent cross-app contamination
  if (returnUrl && referrer) {
    try {
      const returnHost = new URL(returnUrl).hostname.toLowerCase();
      const referrerApp = referrer.replace(/-(?:localhost|dev|qa|alpha|staging|beta)$/, '');
      // Get valid hostname patterns for this referrer (or use the app name itself)
      const validPatterns = REFERRER_HOSTNAME_PATTERNS[referrerApp] || [referrerApp];
      // Check if the returnUrl host contains any of the valid patterns
      const isValid = validPatterns.some((pattern) => returnHost.includes(pattern));
      if (!isValid) {
        console.warn('[SignIn] Discarding returnUrl - does not match referrer:', {
          returnUrl,
          referrer,
          returnHost,
          validPatterns
        });
        // Clear the stale returnUrl from storage
        try {
          localStorage.removeItem('x-sl-sso-signin-return-url');
        } catch {
          /* ignore */
        }
        return undefined;
      }
    } catch {
      // Invalid URL, discard it
      console.warn('[SignIn] Discarding invalid returnUrl:', returnUrl);
      return undefined;
    }
  }

  console.log('[SignIn] resolveReturnUrl:', { urlReturn, storedReturn, result: returnUrl });
  return returnUrl;
}

const SignInV2Container: React.FC<SignInV2ContainerProps> = (props) => {
  const { app, auth, clearErrorMessage, setErrorMessage, history } = props;

  const [authStep, setAuthStep] = useState<AuthStep>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [buttonText, setButtonText] = useState(i18n.button_login);
  const [loading, setLoading] = useState(false);
  const [authProvider, setAuthProvider] = useState<string | null>(null);
  const [v2LoginData, setV2LoginData] = useState<V2LoginResponse | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [otpError, setOtpError] = useState('');
  const [isV1CognitoFlow, setIsV1CognitoFlow] = useState(false);
  // Opaque Cognito CUSTOM_AUTH Session blob returned by /v2/otp/send. Threaded
  // back into /v2/otp/verify so the verify path does not depend on the Redis
  // cache the backend used to maintain (which can be wiped mid-flow by the
  // admin cache-clear endpoint). Refreshed on every send (initial + resend)
  // because Cognito rotates the Session on each fresh InitiateAuth and
  // invalidates the prior one.
  const [challengeSession, setChallengeSession] = useState<string | undefined>(undefined);

  // Persist referrer and returnUrl to localStorage, then check if already authenticated.
  // Also handles V1 Amplify callback when returning from Cognito after a V1 flow redirect.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlReferrer = urlParams.get('referrer');
    const urlReturnUrl = urlParams.get('returnUrl');

    console.log('[SignIn] Mount — URL referrer:', urlReferrer, 'returnUrl:', urlReturnUrl);

    if (urlReferrer) {
      clearAuthFlowData();
      setAuthFlowValue('x-sl-sso-signin-referrer', urlReferrer);
    }
    if (urlReturnUrl) {
      setAuthFlowValue('x-sl-sso-signin-return-url', urlReturnUrl);
    }

    const handleV1Callback = async () => {
      const pendingFlow = sessionStorage.getItem(V1_PENDING_AUTH_KEY);
      if (pendingFlow !== 'v1') return false;

      try {
        initializeAmplify();
        const session = await safelyFetchAuthSession();
        if (!session?.tokens?.accessToken) {
          sessionStorage.removeItem(V1_PENDING_AUTH_KEY);
          return false;
        }

        const accessToken = session.tokens.accessToken.toString();
        const idToken = session.tokens.idToken?.toString() || '';
        const expiresIn = session.tokens.accessToken.payload.exp;

        // V2 helpers give us the stage-suffixed referrer (e.g. "atlas-localhost"),
        // which is needed for the cross-domain redirect back to the right host.
        // But V1 endpoints (LoginExternalAuth + SecretsManager lookup) only know
        // the base app name (e.g. "atlas"). Send the base name to V1.
        const referrer = resolveReferrer();
        const returnUrl = resolveReturnUrl(referrer);
        const v1Referrer = referrer.replace(/-(?:localhost|dev|qa|alpha|staging|beta)$/, '');
        const username = localStorage.getItem('x-sl-signin-user-email') || '';
        const apiHost = getApiHost();

        const response = await axios.post(
          `https://${apiHost}/api/user/LoginExternalAuth`,
          {
            username,
            accessToken,
            refreshToken: idToken,
            expiresIn,
            referrer: v1Referrer
          },
          { withCredentials: true, timeout: 30000 }
        );

        sessionStorage.removeItem(V1_PENDING_AUTH_KEY);

        if (response.data?.session?.status === 'good' || response.data?.ssoJwtToken) {
          if (idToken) {
            setCognitoIdToken(idToken);
          }
          // Auth version is now signaled by the `sl-auth-version` cookie set
          // by /api/user/LoginExternalAuth in the same response. No client
          // write needed — atlas/beacon/etc read the cookie at AppSwitch mount.
          // Mirror the V2 redirect pattern — same handoff to the target app
          // regardless of auth flow. The backend (LoginExternalAuth for V1,
          // v2IssueToken for V2) is responsible for setting whatever cookies
          // the target app needs to establish its session.
          //
          // We also append ?jwt_token=… for V1: Atlas/Beacon ignore it (they
          // rely on .stackline.com cookies), but Advisor's V1 AuthProvider
          // (zenith-ui simple-auth.handleAuthCallback) reads it from the URL
          // and stores it as nova-jwt-token in localStorage. Without it
          // Advisor never recognizes the session and bounces back to SSO.
          let target = appendJwtToken(getAppUrlForReferrer(referrer, returnUrl), response.data?.ssoJwtToken);
          // Forward the Cognito idToken so atlas/beacon can hit Advisor
          // with `Authorization: Bearer <idToken>`. The Amplify session
          // here lives on enterprise-sso and is not visible to the target
          // origin, so a URL-param handoff is required.
          target = appendCognitoIdToken(target, idToken);
          clearAuthFlowData();
          window.location.href = target;
          return true;
        }
      } catch (err) {
        console.error('[SignIn] V1 callback handling failed:', err);
        sessionStorage.removeItem(V1_PENDING_AUTH_KEY);
      }
      return false;
    };

    const checkExistingAuth = async () => {
      const handledV1 = await handleV1Callback();
      if (handledV1) return;

      try {
        // Lightweight session-info check is enough on this path -- we only
        // need to know whether the user is already authenticated. Division
        // metadata for the redirect is resolved separately downstream
        // (e.g. by the target app's SSR / division dropdown).
        const response = await v2SessionInfo();
        console.log('[SignIn] v2SessionInfo result:', response.authenticated, 'divisionId:', response.divisionId);

        if (response.authenticated && response.divisionId) {
          const referrer = resolveReferrer();
          const returnUrl = resolveReturnUrl(referrer);
          console.log('[SignIn] Already authenticated — referrer:', referrer, 'returnUrl:', returnUrl);

          clearAuthFlowData();

          if (isSsoHost(window.location.host)) {
            // Direct visit with no app context -- the user typed the SSO host
            // rather than being bounced here by an app. Show the launcher
            // instead of guessing an app for them. Anything carrying a
            // ?referrer keeps the straight-through redirect below, so no
            // existing flow gains a click.
            if (!urlReferrer) {
              console.log('[SignIn] Already authenticated, no referrer -- showing the app launcher');
              history.replace('/apps');
              return;
            }

            const target = getAppUrlForReferrer(referrer, returnUrl);
            // SSO-only referrers (e.g. GitBook) need a Stackline-Inc SSO JWT
            // appended to the redirect; V2 cookie alone is not consumable there.
            // TODO(v1-deprecation): swap fetchSsoJwtToken for a V2 SSO-token
            // endpoint once available. See main TODO at fetchSsoJwtToken().
            const ssoJwtToken = await fetchSsoJwtToken(referrer);
            const finalTarget = appendJwtToken(target, ssoJwtToken);
            console.log('[SignIn] Redirecting to:', finalTarget);
            window.location.href = finalTarget;
          } else {
            history.push('/');
          }
          return;
        }
        console.log('[SignIn] Not authenticated — showing login form');
      } catch {
        console.log('[SignIn] v2SessionInfo failed — showing login form');
      } finally {
        setCheckingAuth(false);
      }
    };

    checkExistingAuth();
  }, [history]);

  // Pick up login data stored by AuthCallback (multi-division flow)
  useEffect(() => {
    const storedLoginData = sessionStorage.getItem('sl-v2-login-data');
    if (storedLoginData) {
      try {
        const loginData = JSON.parse(storedLoginData);
        sessionStorage.removeItem('sl-v2-login-data');

        // AuthCallback passes referrer/returnUrl alongside login data
        // so we don't depend on localStorage (which is shared across tabs)
        if (loginData._referrer) {
          setAuthFlowValue('x-sl-sso-signin-referrer', loginData._referrer);
        }
        if (loginData._returnUrl) {
          setAuthFlowValue('x-sl-sso-signin-return-url', loginData._returnUrl);
        }

        setV2LoginData(loginData as V2LoginResponse);
        setEmail(loginData.email);

        const defaultDiv = resolveDefaultDivision(loginData.divisions, loginData.lastActiveDivisionId);
        handleDivisionSelect(defaultDiv.id, loginData as V2LoginResponse);
      } catch {
        sessionStorage.removeItem('sl-v2-login-data');
      }
    }
  }, []);

  const handleDivisionSelect = async (divisionId: string, loginData?: V2LoginResponse) => {
    try {
      setAuthStep('issuing_token');
      setLoading(true);

      const data = loginData || v2LoginData;
      if (!data) {
        throw new Error('No login data available');
      }

      const referrer = resolveReferrer();
      const returnUrl = resolveReturnUrl(referrer);
      // Captured before clearAuthFlowData() below wipes the stored referrer.
      const hadAppContext = !!rawReferrer();
      console.log('[SignIn] handleDivisionSelect — referrer:', referrer, 'returnUrl:', returnUrl);
      await v2IssueToken(data.sessionToken, divisionId, referrer);

      // Auth version is now signaled by the `sl-auth-version=v2` cookie set
      // by /api/auth/v2/token in the same response. No client write needed.
      saveDivisions(data.divisions);
      saveCurrentDivisionId(divisionId);
      clearAuthFlowData();

      if (isSsoHost(window.location.host)) {
        if (!hadAppContext) {
          history.replace('/apps');
          return;
        }
        const target = getAppUrlForReferrer(referrer, returnUrl);
        // TODO(v1-deprecation): use response.data?.ssoJwtToken from
        // v2IssueToken once /api/auth/v2/token returns it inline.
        // See main TODO at fetchSsoJwtToken().
        const ssoJwtToken = await fetchSsoJwtToken(referrer);
        window.location.href = appendJwtToken(target, ssoJwtToken);
        return;
      }

      history.push({ pathname: '/' });
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to select division. Please try again.');
      setAuthStep('division_select');
    } finally {
      setLoading(false);
    }
  };

  const handleV1PasswordLogin = async () => {
    setLoading(true);
    clearErrorMessage();
    try {
      const referrer = resolveReferrer();
      // V1 endpoints expect the base referrer (no -localhost/-dev/-qa/etc suffix).
      const v1Referrer = referrer.replace(/-(?:localhost|dev|qa|alpha|staging|beta)$/, '');
      const response = await axios.post(
        `https://${getApiHost()}/api/user/Login`,
        { username: email, password, referrer: v1Referrer },
        { headers: { Authorization: `Basic ${btoa(`${email}:${password}`)}` } }
      );

      const { session, ssoJwtToken, cognitoIdToken } = response.data;
      if (session?.status === 'bad') {
        setErrorMessage(
          session.statusMessage === 'passwordExpired'
            ? 'Your password has expired. Please reset it.'
            : 'Invalid username or password.'
        );
        setLoading(false);
        return;
      }

      if (cognitoIdToken) {
        setCognitoIdToken(cognitoIdToken);
      }

      // Auth version is now signaled by the `sl-auth-version=v1` cookie set
      // by /api/user/Login in the same response. No client write needed.

      const returnUrl = resolveReturnUrl(referrer);
      // Mirror V2 redirect — backend handles V1 vs V2 cookie/token differences.
      // Append ssoJwtToken for Advisor's URL-token V1 flow (see V1-callback
      // comment above for context). Harmless for Atlas/Beacon.
      if (isSsoHost(window.location.host)) {
        if (!rawReferrer()) {
          history.replace('/apps');
          return;
        }
        let target = appendJwtToken(getAppUrlForReferrer(referrer, returnUrl), ssoJwtToken);
        // Forward the Cognito idToken so atlas/beacon can hit Advisor
        // with `Authorization: Bearer <idToken>`. setCognitoIdToken above
        // persists to enterprise-sso's sessionStorage, which is not visible
        // to the target origin — only a URL-param handoff survives.
        target = appendCognitoIdToken(target, cognitoIdToken);
        window.location.href = target;
      } else {
        history.push('/');
      }
    } catch {
      setErrorMessage('Login failed. Please try again.');
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    clearErrorMessage();

    try {
      if (!email) {
        setErrorMessage('Please enter your email address');
        setLoading(false);
        return;
      }

      if (isV1CognitoFlow && password) {
        await handleV1PasswordLogin();
        return;
      }

      const response = await axios.post(`https://${getApiHost()}/api/user/LoginAuthProvider`, { username: email });

      const currentAuthProvider = response.data.loginAuthProvider;
      const useV2Flow = evaluateV2ForEmail(email);

      if (useV2Flow) {
        if (currentAuthProvider !== 'cognito') {
          localStorage.setItem('x-sl-signin-user-email', email);
          setAuthProvider(currentAuthProvider);
          setAuthStep('redirecting');

          const referrer = resolveReferrer();
          const returnUrl = resolveReturnUrl(referrer);
          // null, not 'atlas', when nothing was supplied — AuthCallback reads
          // this back to decide between an app and the launcher.
          const state = { referrer: rawReferrer() ? referrer : null, returnUrl };

          await redirectToCognito(currentAuthProvider, state);
        } else {
          try {
            const sendResp = await v2OtpSend(email);
            setChallengeSession(sendResp.challengeSession);
            setAuthStep('otp_verify');
            setOtpError('');
            setLoading(false);
          } catch (err: any) {
            const msg = err?.response?.data?.error_description || 'Failed to send verification code. Please try again.';
            setErrorMessage(msg);
            setLoading(false);
          }
        }
      } else {
        localStorage.setItem('x-sl-signin-user-email', email);

        if (currentAuthProvider !== 'cognito') {
          sessionStorage.setItem(V1_PENDING_AUTH_KEY, 'v1');
          initializeAmplify();
          // Don't call V1's saveSSOReferrer — it overwrites the V2-format
          // localStorage value with a plain string, which causes V2's
          // getAuthFlowValue to JSON.parse-throw and remove the key entirely.
          // The V2 mount useEffect already saved the referrer in JSON format.
          setAuthStep('v1_redirect');

          const referrer = resolveReferrer();
          const result = await amplifyFederatedSignIn({ custom: currentAuthProvider }, JSON.stringify({ referrer }));
          if (!result) {
            sessionStorage.removeItem(V1_PENDING_AUTH_KEY);
            setAuthStep('email');
            setLoading(false);
          }
        } else {
          setAuthProvider('cognito');
          setIsV1CognitoFlow(true);
          setLoading(false);
        }
      }
    } catch {
      setErrorMessage('Failed to check authentication provider. Please try again.');
      setAuthStep('email');
      setLoading(false);
    }
  };

  const handleOtpVerify = async (code: string) => {
    setLoading(true);
    setOtpError('');

    try {
      // Pass referrer so the backend can compute an app-aware default division
      // (lastActiveDivisionId) -- e.g. when signing in via omni.stackline.com,
      // prefer a division that has omni access. resolveReferrer() returns the
      // same value used in handleDivisionSelect below for consistency.
      const referrer = resolveReferrer();
      const loginData = await v2OtpVerify(email, code, challengeSession, referrer);
      setV2LoginData(loginData);

      const defaultDiv = resolveDefaultDivision(loginData.divisions, loginData.lastActiveDivisionId);
      handleDivisionSelect(defaultDiv.id, loginData);
    } catch (err: any) {
      // The code was accepted; the account just has nowhere to go. Without this
      // branch the message below tells the user their correct code was invalid
      // and sends them round to request another one.
      if (isNoWorkspaceError(err)) {
        setAuthStep('no_workspace');
        setLoading(false);
        return;
      }
      const msg = err?.response?.data?.error_description || 'Invalid or expired code. Please try again.';
      setOtpError(msg);
      setLoading(false);
    }
  };

  const handleOtpResend = async () => {
    setOtpError('');
    try {
      // Cognito rotates the Session blob on every fresh InitiateAuth and
      // invalidates the previous one. Overwrite the stored value so the next
      // verify uses the latest Session -- otherwise we'd present a Cognito-
      // invalidated Session and the verify would fail.
      const sendResp = await v2OtpSend(email);
      setChallengeSession(sendResp.challengeSession);
    } catch (err: any) {
      const msg = err?.response?.data?.error_description || 'Failed to resend code. Please try again.';
      setOtpError(msg);
    }
  };

  const handleSignInChange = useCallback(
    (value: any) => {
      clearErrorMessage();
      if (value.email !== undefined) setEmail(value.email);
      if (value.password !== undefined) setPassword(value.password);
      if (value.buttonText !== undefined) setButtonText(value.buttonText);
      if (value.loading !== undefined) setLoading(value.loading);
      if (value.authProvider !== undefined) setAuthProvider(value.authProvider);
    },
    [clearErrorMessage]
  );

  const handleEmailChange = useCallback((newEmail: string) => {
    setEmail(newEmail?.trim());
  }, []);

  if (authStep === 'no_workspace') {
    return (
      <NoWorkspace
        email={email || localStorage.getItem('x-sl-signin-user-email') || undefined}
        onStartOver={() => {
          setAuthStep('email');
          setOtpError('');
          setErrorMessage('');
        }}
      />
    );
  }

  if (authStep === 'otp_verify') {
    return (
      <BeaconSignInContainer appName={app.name} notificationMSG={auth.message}>
        <OtpVerify
          email={email}
          onVerify={handleOtpVerify}
          onResend={handleOtpResend}
          loading={loading}
          error={otpError}
        />
      </BeaconSignInContainer>
    );
  }

  if (checkingAuth || authStep === 'redirecting' || authStep === 'issuing_token' || authStep === 'v1_redirect') {
    return (
      <BeaconSignInContainer appName={app.name} notificationMSG={auth.message}>
        <Loading className="spinner" />
      </BeaconSignInContainer>
    );
  }

  if (authStep === 'division_select' && v2LoginData) {
    return (
      <div style={{ width: '100%', height: '100%', backgroundColor: '#f5f5f5' }}>
        <DivisionSelector
          userName={v2LoginData.name}
          userEmail={v2LoginData.email}
          divisions={v2LoginData.divisions}
          lastActiveDivisionId={v2LoginData.lastActiveDivisionId}
          onSelectDivision={handleDivisionSelect}
          loading={loading}
          appName={app.name}
        />
      </div>
    );
  }

  return (
    <BeaconSignInContainer appName={app.name} notificationMSG={auth.message}>
      <SignIn
        app={app}
        buttonText={buttonText}
        handleChange={handleSignInChange}
        handleFormSubmit={handleEmailSubmit}
        loading={loading}
        email={email}
        authProvider={authProvider}
        handleEmailChange={handleEmailChange}
      />
    </BeaconSignInContainer>
  );
};

const mapStateToProps = (state: any) => ({
  app: state.app,
  auth: state.auth
});

const mapDispatchToProps = {
  clearErrorMessage: authOperations.clearMessage,
  setErrorMessage: authOperations.setErrorMessage
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SignInV2Container));
